<script setup lang="ts">
import {useRoute, useRouter} from "vue-router";
import {onMounted, onUnmounted, ref, watch} from "vue";
import {extensionApi, programApi} from "@/container";
import Extension from "@/core/interfaces/extension/Extension";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import MarkdownView from "@/core/components/markdown/Markdown.vue";
import {SupportProgramMarketStatistic} from "@/program/interfaces/Program";
import SupportProgramExtensionDetails
    from "@/extension/components/support-program-extension-details/SupportProgramExtensionDetails.vue";
import StripeCheckoutService from "@/stripe/services/stripeCheckoutService";
import StripePriceService from "@/stripe/services/stripePriceService";
import ExtensionDetailsHeader from "@/extension/components/extension-details-header/ExtensionDetailsHeader.vue";
import ExtensionService from "@/extension/services/ExtensionService";
import ExpandableTextOverflow from "@/core/components/expandable-text-overflow/ExpandableTextOverflow.vue";
import ExtensionSlider from "@/extension/components/extension-slider/ExtensionSlider.vue";
import ExtensionDetailsPotentialShowcase
    from "@/extension/components/extension-details-header/ExtensionDetailsPotentialShowcase.vue";
import ExtensionNavigationService from "@/extension/services/ExtensionNavigationService";
import {useExtensionStore} from "@/stores/extension.store";

const props = defineProps<{
    extensionId: string
}>();

const router = useRouter();
const route = useRoute();
const extensionId = Number(props.extensionId);
const loading = ref(false);
const loadingStats = ref(false);
const extension = ref<null | Extension>(null);
const statistics = ref<undefined | SupportProgramMarketStatistic[]>(undefined);
const extensionStore = useExtensionStore();
const loadingToggle = ref(false);

/**
 * Read the extension.
 *
 * @param extensionId
 */
const readExtension = async (extensionId: number): Promise<Extension> => {
    return extensionApi()
        .read(extensionId);
};

/**
 * Read the market statistics of the support program.
 */
const readMarketStats = async (supportProgramId: number) => {
    loadingStats.value = true;
    return programApi()
        .statistics(supportProgramId)
        .then(_statistics => {
            statistics.value = _statistics;
        })
        .finally(() => {
            loadingStats.value = false;
        });
};

/**
 * Load the extension data.
 */
const load = async (extensionId: number) => {
    loading.value = true;
    return readExtension(extensionId)
        .then(response => {
            extension.value = response;
        })
        .finally(() => {
            loading.value = false;
        });
};

watch(() => extension.value, () => {
    if (extension.value && ExtensionService.isSupportProgramExtension(extension.value)) {
        return readMarketStats(extension.value.sub_id);
    }

    return;
});

watch(() => extensionId, async (id: number) => {
    await load(id);
}, {immediate: true});

onMounted(() => {
    window.ipcRenderer.on("stripe-checkout-success", () => {
        load(extensionId);
    });
});

onUnmounted(() => {
    window.ipcRenderer.removeAllListeners("stripe-checkout-success");
});

/**
 * Buy the extension.
 */
const buyExtension = async (extension: Extension) => {
    const price = StripePriceService.featuredPrice(extension.prices);

    if (price) {
        return StripeCheckoutService.checkout(price);
    }
};

/**
 * Toggle the activation of the extension.
 */
const toggleActivation = () => {
    if (!extension.value) {
        return;
    }

    const nextAction = ExtensionService.getNextAction(extension.value);

    if (nextAction.action === 'disable') {
        loadingToggle.value = true;
        return extensionStore.disableExtension(extension.value.id)
            .then(() => load(extensionId))
            .finally(() => {
                loadingToggle.value = false;
            });
    } else if (nextAction.action === 'enable') {
        loadingToggle.value = true;
        return extensionStore.enableExtension(extension.value.id)
            .then(() => load(extensionId))
            .then(() => {
                // This modal only advertises the module. Once it is activated
                // the pharmacist belongs elsewhere — and leaving the route
                // closes the modal on its own.
                if (!extension.value) {
                    return;
                }

                // A module with something to set up has a page of its own, in
                // which its description and its configurator sit together,
                // whatever the kind of module. The others go straight to work.
                if (extension.value.configurator) {
                    router.push({
                        name: 'extension.detail',
                        params: {extensionId: String(extension.value.id)}
                    });

                    return;
                }

                ExtensionNavigationService.navigate(extension.value, router, route);
            })
            .finally(() => {
                loadingToggle.value = false;
            });
    } else if (nextAction.action === 'buy') {
        if (extension.value.subscription_plan) {
            router.push({
                name: 'subscription-plan',
                params: {
                    planId: extension.value.subscription_plan.id.toString()
                }
            });
        } else {
            buyExtension(extension.value);
        }
    }
};
</script>

<template>
  <BaseContainer>
    <BaseFlexSpinner v-if="loading" />
    <div v-else-if="extension">
      <ExtensionDetailsHeader :extension="extension" :loading="loadingToggle" @button-click="toggleActivation" />
      <ExtensionDetailsPotentialShowcase :extension-id="extension.id" />
      <div class="p-6 bg-lightPurple-lightest flex flex-row space-between">
        <expandable-text-overflow class="max-w-screen w-full">
          <MarkdownView :content="extension.description" />
        </expandable-text-overflow>
        <div v-if="extension.video_url || extension.previews?.length" class="w-64 pl-4">
          <extension-slider :extension="extension" class="w-24" />
        </div>
      </div>
      <BaseFlexSpinner v-if="loadingStats" />
      <SupportProgramExtensionDetails
        v-else-if="ExtensionService.isSupportProgramExtension(extension)"
        :extension="extension"
        :market-stats="statistics"
        @activate="toggleActivation"
      />
    </div>
  </BaseContainer>
</template>

<style scoped>
</style>
